import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { format, addDays, isAfter, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { voucherService } from '@/services/voucherService';
import { useAuthContext } from '@/contexts/AuthContext';
import { Voucher, VoucherStatus } from '@/types/voucher';
import { supabase } from '@/lib/supabase';
import {
  Check,
  Ban,
  Send,
  MoreHorizontal,
  Plus,
  Copy,
  User,
  Search,
  CalendarClock,
  Award,
  BookCheck,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Clipboard,
  ChevronDown,
  RefreshCw,
  Ticket
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { generateUniqueCode, generateVoucherCodeValue } from '@/lib/dbUtils';
import { Progress } from '@/components/ui/progress';

interface Subscription {
  id: string;
  name: string;
}

interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
  job_title?: string | null;
}

// Adicionar uma interface para representar a estrutura esperada da resposta da API
interface CustomerRelationshipResponse {
  customer_relationship?: {
    customer?: {
      name?: string;
    };
  };
  id: string;
}

interface VoucherStats {
  total: number;
  active: number;
  claimed: number;
  cancelled: number;
  expired: number;
}

const EXAM_TYPES = [
  { value: 'mos', label: 'Microsoft Office Specialist (MOS)' },
  { value: 'mce', label: 'Microsoft Certified Educator (MCE)' },
  { value: 'mcp', label: 'Microsoft Certified Professional (MCP)' },
  { value: 'mcf', label: 'Microsoft Certified Fundamentals (MCF)' },
];

const VoucherCodes = () => {
  const { user } = useAuthContext();
  const [voucherCodes, setVoucherCodes] = useState<Voucher[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [customerContacts, setCustomerContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [examFilter, setExamFilter] = useState<string>('all');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [stats, setStats] = useState<VoucherStats>({
    total: 0,
    active: 0,
    claimed: 0,
    cancelled: 0,
    expired: 0
  });

  // Estado do formulário para criar um novo código voucher
  const [newVoucherCode, setNewVoucherCode] = useState({
    code: '',
    exam: '',
    subscription_id: '',
    expires_at: addDays(new Date(), 365).toISOString(), // Expiração em 365 dias
    status: 'active' as VoucherStatus,
    issued_to: 'none', // ID do contato do cliente
  });

  // Adicionar estados para o modal de criação em massa
  const [isBulkCreateDialogOpen, setIsBulkCreateDialogOpen] = useState(false);
  const [bulkCreateCount, setBulkCreateCount] = useState(10); // Quantidade padrão: 10
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);

  // Adicionar estado para os dados do formulário de criação em massa
  const [bulkVoucherData, setBulkVoucherData] = useState({
    exam: '',
    subscription_id: '',
    expires_at: addDays(new Date(), 365).toISOString(),
    issued_to: 'none', // ID do contato do cliente
  });

  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  // New state for assign recipient dialog
  const [isAssignRecipientDialogOpen, setIsAssignRecipientDialogOpen] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<string>('none');
  const [isAssigningRecipient, setIsAssigningRecipient] = useState(false);

  // Calculate stats from voucher codes
  const calculateStats = (vouchers: Voucher[]) => {
    const now = new Date();
    
    const active = vouchers.filter(v => v.status === 'active' && (!v.expires_at || new Date(v.expires_at) > now)).length;
    const expired = vouchers.filter(v => v.status === 'active' && v.expires_at && new Date(v.expires_at) <= now).length;
    const claimed = vouchers.filter(v => v.status === 'claimed').length;
    const cancelled = vouchers.filter(v => v.status === 'cancelled').length;
    
    setStats({
      total: vouchers.length,
      active,
      claimed,
      cancelled,
      expired
    });
  };

  // Buscar códigos de voucher
  useEffect(() => {
    const fetchVoucherCodes = async () => {
      try {
        setLoading(true);
        const voucherCodesData = await voucherService.getVouchers();
        setVoucherCodes(voucherCodesData);
        calculateStats(voucherCodesData);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar códigos de voucher:', error);
        toast.error('Falha ao buscar códigos de voucher');
        setLoading(false);
      }
    };

    fetchVoucherCodes();
  }, []);

  // Buscar assinaturas
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select(`
            id,
            customer_relationship:customer_relationship_id (
              customer:customer_id (
                name
              )
            )
          `);

        if (error) throw error;
        
        // Transformar os dados no formato esperado pela interface
        const formattedSubscriptions: Subscription[] = (data || []).map(sub => {
          // Utilizar assertiva de tipo para o objeto retornado pelo Supabase
          const subWithRelation = sub as CustomerRelationshipResponse;
          
          // Verificar se os dados aninhados existem
          const customerName = 
            subWithRelation.customer_relationship?.customer?.name ||
            `Assinatura ${subWithRelation.id.substring(0, 8)}`;
          
          return {
            id: subWithRelation.id,
            name: customerName
          };
        });
        
        setSubscriptions(formattedSubscriptions);
      } catch (error) {
        console.error('Erro ao buscar assinaturas:', error);
        toast.error('Falha ao carregar lista de assinaturas');
      }
    };

    fetchSubscriptions();
  }, []);

  // Função para buscar contatos de cliente com base na assinatura selecionada
  const fetchCustomerContacts = async (subscriptionId: string) => {
    try {
      setLoadingContacts(true);
      
      // Primeiro, precisamos obter o customer_id da assinatura
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('customer_relationship_id')
        .eq('id', subscriptionId)
        .single();
      
      if (subscriptionError) throw subscriptionError;
      
      if (!subscriptionData || !subscriptionData.customer_relationship_id) {
        setCustomerContacts([]);
        return;
      }
      
      // Agora, precisamos obter o customer_id da relação de cliente
      const { data: relationshipData, error: relationshipError } = await supabase
        .from('customer_relationships')
        .select('customer_id')
        .eq('id', subscriptionData.customer_relationship_id)
        .single();
        
      if (relationshipError) throw relationshipError;
      
      if (!relationshipData || !relationshipData.customer_id) {
        setCustomerContacts([]);
        return;
      }
      
      // Finalmente, buscar os contatos do cliente
      const { data: contacts, error: contactsError } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', relationshipData.customer_id)
        .eq('is_active', true);
        
      if (contactsError) throw contactsError;
      
      setCustomerContacts(contacts || []);
    } catch (error) {
      console.error('Erro ao buscar contatos do cliente:', error);
      toast.error('Não foi possível carregar os contatos do cliente');
      setCustomerContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Lidar com mudanças no formulário
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVoucherCode({ ...newVoucherCode, [name]: value });
  };

  // Lidar com mudanças em seletores
  const handleSelectChange = (name: string, value: string) => {
    setNewVoucherCode({ ...newVoucherCode, [name]: value });
    
    // Se o campo for subscription_id, buscar contatos do cliente
    if (name === 'subscription_id' && value) {
      fetchCustomerContacts(value);
    }
  };

  // Generate a unique code for the *single* creation form
  const generateSingleUniqueCode = async () => {
    setIsGeneratingCode(true);
    try {
      const newCode = await generateUniqueCode({
        tableName: 'vouchers',
        columnName: 'code',
        generateCodeFunction: generateVoucherCodeValue,
        prefix: 'MX'
      });
      setNewVoucherCode({ ...newVoucherCode, code: newCode });
    } catch (error) {
      console.error('Erro ao gerar código único:', error);
      toast.error('Não foi possível gerar um código único');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Abrir o modal de criação
  const handleOpenCreateDialog = () => {
    setNewVoucherCode({
      code: '', // Code will be generated
      exam: '',
      subscription_id: '',
      expires_at: addDays(new Date(), 365).toISOString(),
      status: 'active',
      issued_to: 'none',
    });
    setCustomerContacts([]);
    setIsCreateDialogOpen(true);
    generateSingleUniqueCode(); // Generate code when dialog opens
  };

  // Criar código de voucher
  const handleCreateVoucherCode = async () => {
    try {
      if (!user) {
        toast.error('Você precisa estar logado para criar códigos de voucher');
        return;
      }

      if (!newVoucherCode.code || !newVoucherCode.exam || !newVoucherCode.subscription_id) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      setIsCreating(true);
      const voucherToCreate = {
        ...newVoucherCode,
        created_by: user!.id,
        issued_to: newVoucherCode.issued_to === 'none' ? null : newVoucherCode.issued_to
      };

      const newVoucherCodeData = await voucherService.createVoucher(voucherToCreate);
      setVoucherCodes([newVoucherCodeData, ...voucherCodes]);
      calculateStats([newVoucherCodeData, ...voucherCodes]);
      setIsCreateDialogOpen(false);
      toast.success('Código de voucher criado com sucesso');
    } catch (error: any) {
      console.error('Erro ao criar código de voucher:', error);
      if (error?.code === '23505') {
          toast.error('Falha ao criar: Código de voucher já existe. Tente gerar um novo.');
          await generateSingleUniqueCode(); 
      } else {
          toast.error(`Falha ao criar código de voucher: ${error.message || 'Erro desconhecido'}`);
      }
    } finally {
        setIsCreating(false);
    }
  };

  // Função para lidar com mudanças no formulário de criação em massa
  const handleBulkInputChange = (name: string, value: string) => {
    setBulkVoucherData({
      ...bulkVoucherData,
      [name]: value,
    });
    
    // Se o campo for subscription_id, buscar contatos do cliente
    if (name === 'subscription_id' && value) {
      fetchCustomerContacts(value);
    }
  };

  // Função para criar vouchers em massa
  const handleBulkCreateVouchers = async () => {
    try {
      if (!user) {
        toast.error('Você precisa estar logado para criar códigos de voucher');
        return;
      }

      if (!bulkVoucherData.exam || !bulkVoucherData.subscription_id) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      setIsBulkCreating(true);
      setBulkProgress(0);
      
      const createdVouchers: Voucher[] = [];
      const failedCodes: { reason: string }[] = [];
      
      for (let i = 0; i < bulkCreateCount; i++) {
        try {
          const newCode = await generateUniqueCode({
            tableName: 'vouchers',
            columnName: 'code',
            generateCodeFunction: generateVoucherCodeValue,
            prefix: 'MX'
          });
          
          const voucherToCreate = {
            code: newCode,
            exam: bulkVoucherData.exam,
            subscription_id: bulkVoucherData.subscription_id,
            expires_at: bulkVoucherData.expires_at,
            status: 'active' as VoucherStatus,
            created_by: user!.id,
            issued_to: bulkVoucherData.issued_to === 'none' ? null : bulkVoucherData.issued_to,
          };
          
          const newVoucher = await voucherService.createVoucher(voucherToCreate);
          createdVouchers.push(newVoucher);
        } catch (error: any) {
          console.error(`Erro ao criar voucher ${i+1}:`, error);
          failedCodes.push({ reason: error.message || 'Erro desconhecido' });
        }
        setBulkProgress(Math.round(((i + 1) / bulkCreateCount) * 100));
      }
      
      if (createdVouchers.length > 0) {
        const updatedVouchers = [...createdVouchers, ...voucherCodes];
        setVoucherCodes(updatedVouchers);
        calculateStats(updatedVouchers);
        toast.success(`${createdVouchers.length} códigos de voucher criados com sucesso`);
      }
      
      if (failedCodes.length > 0) {
        toast.error(`${failedCodes.length} códigos não puderam ser criados.`);
        console.warn('Falhas na criação em massa de vouchers:', failedCodes);
      }
      
      setIsBulkCreating(false);
      setIsBulkCreateDialogOpen(false);
    } catch (error) {
      console.error('Erro ao criar vouchers em massa:', error);
      setIsBulkCreating(false);
      
      toast.error('Falha ao criar vouchers em massa');
    }
  };

  // Atualizar status
  const handleUpdateStatus = async (id: string, newStatus: VoucherStatus) => {
    try {
      if (!user) {
        toast.error('Você precisa estar logado para atualizar códigos de voucher');
        return;
      }

      // Atualizar status do voucher no banco de dados
      const updatedVoucher = await voucherService.updateVoucher(id, {
        status: newStatus,
        updated_by: user.id,
        // Se o status for reivindicado, adicionar data de reivindicação
        ...(newStatus === 'claimed' && { claimed_date: new Date().toISOString() })
      });

      // Atualizar lista de códigos de voucher
      setVoucherCodes(
        voucherCodes.map((voucher) => 
          voucher.id === id ? updatedVoucher : voucher
        )
      );

      toast.success('Status do código de voucher atualizado com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar status do código de voucher:', error);
      toast.error('Falha ao atualizar status do código de voucher');
    }
  };

  // Adicionar função para reenviar voucher
  const handleResendVoucher = async (voucherId: string) => {
    console.log('Resend voucher requested for:', voucherId);
    toast.info('A função de reenviar voucher ainda não foi implementada.');
  };

  // Função auxiliar para formatar o nome completo do contato
  const formatContactName = (contact: CustomerContact): string => {
    return `${contact.first_name} ${contact.last_name || ''}`.trim();
  };

  // Função para obter o nome do destinatário pelo ID
  const getIssuedToName = (issuedToId: string | null) => {
    if (!issuedToId) return '-';
    
    // Procurar o contato nos contatos já carregados
    const contact = customerContacts.find(c => c.id === issuedToId);
    if (contact) {
      return formatContactName(contact);
    }
    
    return `ID: ${issuedToId.substring(0, 8)}...`;
  };

  // Filter voucher codes based on search term, status, and date range
  const getFilteredVoucherCodes = () => {
    const now = new Date();
    return voucherCodes.filter((code) => {
    const matchesSearch =
        searchTerm === '' ||
      code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (code.exam?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (code.subscription_id?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    
      const matchesTab = 
        selectedTab === 'all' || 
        (selectedTab === 'active' && code.status === 'active' && (!code.expires_at || new Date(code.expires_at) > now)) ||
        (selectedTab === 'expired' && code.status === 'active' && code.expires_at && new Date(code.expires_at) <= now) ||
        (selectedTab === 'claimed' && code.status === 'claimed') ||
        (selectedTab === 'cancelled' && code.status === 'cancelled');
      
      const matchesStatus = 
        statusFilter === 'all' || 
        (statusFilter === 'active' && code.status === 'active' && (!code.expires_at || new Date(code.expires_at) > now)) ||
        (statusFilter === 'expired' && code.status === 'active' && code.expires_at && new Date(code.expires_at) <= now) ||
        code.status === statusFilter;
      
      const matchesExam = examFilter === 'all' || code.exam === examFilter;
      
      const matchesDateRange = 
        !dateRange?.from || !dateRange?.to || 
        (code.expires_at && 
          isAfter(new Date(code.expires_at), dateRange.from) && 
          isBefore(new Date(code.expires_at), addDays(dateRange.to, 1)));
      
      return matchesSearch && matchesTab && matchesStatus && matchesExam && matchesDateRange;
  });
  };

  const filteredVoucherCodes = getFilteredVoucherCodes();

  // Verificar se um código de voucher está expirado
  const isExpired = (expiresAt: string | null) => {
    return expiresAt && new Date(expiresAt) < new Date();
  };

  // Traduzir status para português
  const translateStatus = (status: string, isExpiredVoucher: boolean = false) => {
    if (isExpiredVoucher && status === 'active') return 'Expirado';
    
    switch(status) {
      case 'active': return 'Ativo';
      case 'claimed': return 'Utilizado';
      case 'expired': return 'Expirado';
      case 'cancelled': return 'Cancelado';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Obter a classe CSS para o status do voucher
  const getStatusClass = (status: string, isExpiredVoucher: boolean) => {
    if (status === 'active') {
      if (isExpiredVoucher) {
        return 'bg-yellow-100 text-yellow-800'; // Expirado, mas ainda marcado como ativo
      }
      return 'bg-green-100 text-green-800'; // Ativo
    } else if (status === 'claimed') {
      return 'bg-blue-100 text-blue-800'; // Utilizado
    } else if (status === 'expired') {
      return 'bg-yellow-100 text-yellow-800'; // Expirado
    } else if (status === 'cancelled') {
      return 'bg-red-100 text-red-800'; // Cancelado
    }
    return 'bg-gray-100 text-gray-800'; // Fallback
  };

  // Adicionar uma função para obter o nome completo do exame a partir do código
  const getExamFullName = (examCode: string | null): string => {
    if (!examCode) return 'N/A';
    
    const examType = EXAM_TYPES.find(type => type.value === examCode);
    return examType ? examType.label : examCode;
  };

  // Adicionar uma função para calcular dias até a expiração
  const getDaysUntilExpiration = (expiresAt: string | null): number | null => {
    if (!expiresAt) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalizar para início do dia
    
    const expirationDate = new Date(expiresAt);
    expirationDate.setHours(0, 0, 0, 0); // Normalizar para início do dia
    
    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Adicionar uma função para formatação visual dos dias restantes
  const formatDaysRemaining = (days: number | null): JSX.Element => {
    if (days === null) return <span>--</span>;
    
    if (days < 0) {
      return <span className="text-red-500">Expirado ({Math.abs(days)} dias atrás)</span>;
    } else if (days === 0) {
      return <span className="text-orange-500">Expira hoje</span>;
    } else if (days <= 7) {
      return <span className="text-orange-500">{days} dias</span>;
    } else if (days <= 30) {
      return <span className="text-yellow-600">{days} dias</span>;
    } else {
      return <span className="text-green-600">{days} dias</span>;
    }
  };

  // Function to open assign recipient dialog
  const handleOpenAssignRecipientDialog = (voucherId: string) => {
    setSelectedVoucherId(voucherId);
    setSelectedRecipient('none');
    
    const voucher = voucherCodes.find(v => v.id === voucherId);
    if (voucher && voucher.subscription_id) {
      fetchCustomerContacts(voucher.subscription_id);
    }
    
    setIsAssignRecipientDialogOpen(true);
  };

  // Function to assign recipient to voucher
  const handleAssignRecipient = async () => {
    if (!selectedVoucherId || !selectedRecipient) return;
    
    setIsAssigningRecipient(true);
    
    try {
      const voucher = voucherCodes.find(v => v.id === selectedVoucherId);
      if (!voucher) throw new Error("Voucher não encontrado");
      
      const { error } = await supabase
        .from('vouchers')
        .update({ 
          issued_to: selectedRecipient === 'none' ? null : selectedRecipient,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedVoucherId);
      
      if (error) throw error;
      
      setVoucherCodes(prev => 
        prev.map(v => 
          v.id === selectedVoucherId 
            ? { ...v, issued_to: selectedRecipient === 'none' ? null : selectedRecipient } 
            : v
        )
      );
      
      setIsAssignRecipientDialogOpen(false);
      toast.success("Destinatário atualizado com sucesso");
      
    } catch (error) {
      console.error('Erro ao atribuir destinatário:', error);
      toast.error("Não foi possível atribuir o destinatário");
    } finally {
      setIsAssigningRecipient(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Códigos de Voucher</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Ticket className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Códigos de Voucher</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie seus códigos de voucher para exames e certificações
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={() => setIsBulkCreateDialogOpen(true)} className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200">
                  <Copy className="h-4 w-4 mr-2" /> Criar em Massa
                </Button>
                <Button onClick={handleOpenCreateDialog} className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg">
                  <Plus className="h-4 w-4 mr-2" /> Novo Voucher
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md"><Award className="h-4 w-4 text-blue-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Total</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.total}</div>
                  </div>
                  <p className="text-xs text-gray-500">{EXAM_TYPES.length} tipos de exames</p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md"><CheckCircle className="h-4 w-4 text-green-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Ativos</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.active}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.active / stats.total) * 100 : 0} className="h-1.5" />
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-yellow-100 rounded-md"><AlertTriangle className="h-4 w-4 text-yellow-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Expirados</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.expired}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.expired / stats.total) * 100 : 0} className="h-1.5" />
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-indigo-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-indigo-100 rounded-md"><BookCheck className="h-4 w-4 text-indigo-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Utilizados</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.claimed}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.claimed / stats.total) * 100 : 0} className="h-1.5" />
                </CardContent>
              </Card>
              
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-red-100 rounded-md"><XCircle className="h-4 w-4 text-red-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Cancelados</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.cancelled}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.cancelled / stats.total) * 100 : 0} className="h-1.5" />
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                  <TabsList className="grid grid-cols-5">
                    <TabsTrigger value="all">Todos <Badge variant="outline" className="ml-2">{stats.total}</Badge></TabsTrigger>
                    <TabsTrigger value="active">Ativos <Badge variant="outline" className="ml-2">{stats.active}</Badge></TabsTrigger>
                    <TabsTrigger value="expired">Expirados <Badge variant="outline" className="ml-2">{stats.expired}</Badge></TabsTrigger>
                    <TabsTrigger value="claimed">Utilizados <Badge variant="outline" className="ml-2">{stats.claimed}</Badge></TabsTrigger>
                    <TabsTrigger value="cancelled">Cancelados <Badge variant="outline" className="ml-2">{stats.cancelled}</Badge></TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 text-muted-foreground -translate-y-1/2" />
                    <Input
                      placeholder="Buscar por código, exame..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 bg-white"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={examFilter} onValueChange={setExamFilter}>
                      <SelectTrigger className="w-full sm:w-[180px] h-10 bg-white"><SelectValue placeholder="Tipo de exame" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os exames</SelectItem>
                        {EXAM_TYPES.map(type => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full sm:w-auto h-10 bg-white">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>
                            {dateRange?.from ? (dateRange.to ? `${format(dateRange.from, 'dd/MM/yy')} - ${format(dateRange.to, 'dd/MM/yy')}` : format(dateRange.from, 'dd/MM/yyyy')) : 'Período'}
                          </span>
                          <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><CalendarComponent mode="range" selected={dateRange} onSelect={setDateRange} initialFocus /></PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Exame</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Expira em</TableHead>
                        <TableHead>Dias Restantes</TableHead>
                        <TableHead>Destinatário</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                            <TableCell><Skeleton className="h-9 w-20 ml-auto" /></TableCell>
                        </TableRow>
                        ))
                      ) : filteredVoucherCodes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10">
                            <div className="flex flex-col items-center gap-2">
                              <Clipboard className="h-8 w-8 text-muted-foreground opacity-40" />
                              <div className="text-lg font-extrabold">Nenhum código de voucher encontrado</div>
                              <div className="text-sm text-muted-foreground max-w-md">
                                {searchTerm || statusFilter !== 'all' || examFilter !== 'all' || dateRange ? 
                                  'Tente ajustar os filtros para ver mais resultados.' : 
                                  'Crie seu primeiro voucher clicando no botão "Novo Voucher".'}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredVoucherCodes.map((voucher) => (
                          <TableRow key={voucher.id} className="hover:bg-muted/50">
                            <TableCell className="font-mono font-extrabold">{voucher.code}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Award className="h-4 w-4 text-muted-foreground" />
                                <span>{voucher.exam ? getExamFullName(voucher.exam) : 'N/A'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={cn("capitalize", getStatusClass(voucher.status, isExpired(voucher.expires_at)))}
                              >
                                {translateStatus(voucher.status, isExpired(voucher.expires_at))}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <CalendarClock className="h-4 w-4 text-muted-foreground" />
                                <span>
                                  {voucher.expires_at ? format(new Date(voucher.expires_at), 'dd/MM/yyyy', { locale: ptBR }) : 'Nunca'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>{formatDaysRemaining(getDaysUntilExpiration(voucher.expires_at))}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span>{getIssuedToName(voucher.issued_to)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-end gap-1">
                                {voucher.status === 'active' && !isExpired(voucher.expires_at) && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /><span className="sr-only">Ações</span></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => handleUpdateStatus(voucher.id, 'claimed')} className="text-green-600"><Check className="mr-2 h-4 w-4" />Marcar como Utilizado</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleUpdateStatus(voucher.id, 'cancelled')} className="text-red-600"><Ban className="mr-2 h-4 w-4" />Cancelar</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleOpenAssignRecipientDialog(voucher.id)}><User className="mr-2 h-4 w-4" />Atribuir Destinatário</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleResendVoucher(voucher.id)}><Send className="mr-2 h-4 w-4" />Reenviar</DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                                {(voucher.status !== 'active' || isExpired(voucher.expires_at)) && (
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-500 hover:text-blue-700" onClick={() => handleResendVoucher(voucher.id)} title="Reenviar">
                                    <Send className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">Mostrando {filteredVoucherCodes.length} de {voucherCodes.length} vouchers</div>
              </CardContent>
            </Card>
            <div className="h-16" />
          </div>
        </div>
      </div>
      
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Novo Código de Voucher</DialogTitle>
            <DialogDescription>
              Preencha os detalhes para criar um novo código de voucher.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="code" className="text-right">Código</Label>
              <div className="col-span-3 flex gap-2">
                <Input id="code" name="code" value={newVoucherCode.code} onChange={handleInputChange} className="flex-1" readOnly />
                <Button type="button" variant="outline" onClick={generateSingleUniqueCode} disabled={isGeneratingCode}>
                  {isGeneratingCode ? (<div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>) : ('Gerar')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="exam" className="text-right">Exame</Label>
              <div className="col-span-3">
                <Select value={newVoucherCode.exam} onValueChange={(value) => handleSelectChange('exam', value)}>
                  <SelectTrigger><SelectValue placeholder="Selecione o tipo de exame" /></SelectTrigger>
                  <SelectContent>
                    {EXAM_TYPES.map(type => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subscription_id" className="text-right">Assinatura</Label>
              <div className="col-span-3">
                <Select value={newVoucherCode.subscription_id} onValueChange={(value) => handleSelectChange('subscription_id', value)}>
                  <SelectTrigger><SelectValue placeholder="Selecione a assinatura" /></SelectTrigger>
                  <SelectContent>
                    {subscriptions.map(subscription => (<SelectItem key={subscription.id} value={subscription.id}>{subscription.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="issued_to" className="text-right">Destinatário</Label>
              <div className="col-span-3">
                <Select value={newVoucherCode.issued_to} onValueChange={(value) => handleSelectChange('issued_to', value)} disabled={!newVoucherCode.subscription_id || loadingContacts}>
                  <SelectTrigger><SelectValue placeholder={loadingContacts ? "Carregando contatos..." : "Selecione o destinatário"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum destinatário</SelectItem>
                    {customerContacts.map(contact => (<SelectItem key={contact.id} value={contact.id}>{formatContactName(contact)} {contact.email ? `(${contact.email})` : ''}</SelectItem>))}
                  </SelectContent>
                </Select>
                {newVoucherCode.subscription_id && customerContacts.length === 0 && !loadingContacts && (<p className="text-xs text-amber-500 mt-1">Nenhum contato encontrado para esta assinatura</p>)}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="expires_at" className="text-right">Expiração</Label>
              <div className="col-span-3">
                <Input id="expires_at" type="text" value={format(new Date(newVoucherCode.expires_at), 'dd/MM/yyyy', { locale: ptBR })} readOnly disabled className="bg-gray-50" />
                <p className="text-xs text-gray-500 mt-1">O voucher expira em 365 dias após a criação</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={handleCreateVoucherCode}>Criar Voucher</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isBulkCreateDialogOpen} onOpenChange={setIsBulkCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Vouchers em Massa</DialogTitle>
            <DialogDescription>Preencha os detalhes para criar múltiplos códigos de voucher de uma vez.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="bulk-count" className="text-right">Quantidade</Label>
              <div className="col-span-3">
                <Input id="bulk-count" type="number" min="1" max="100" value={bulkCreateCount} onChange={(e) => setBulkCreateCount(parseInt(e.target.value) || 1)} className="w-full" />
                <p className="text-xs text-gray-500 mt-1">Máximo de 100 vouchers por operação</p>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="bulk-exam" className="text-right">Exame</Label>
              <div className="col-span-3">
                <Select value={bulkVoucherData.exam} onValueChange={(value) => handleBulkInputChange('exam', value)}>
                  <SelectTrigger><SelectValue placeholder="Selecione o tipo de exame" /></SelectTrigger>
                  <SelectContent>
                    {EXAM_TYPES.map(type => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="bulk-subscription" className="text-right">Assinatura</Label>
              <div className="col-span-3">
                <Select value={bulkVoucherData.subscription_id} onValueChange={(value) => handleBulkInputChange('subscription_id', value)}>
                  <SelectTrigger><SelectValue placeholder="Selecione a assinatura" /></SelectTrigger>
                  <SelectContent>
                    {subscriptions.map(subscription => (<SelectItem key={subscription.id} value={subscription.id}>{subscription.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="bulk-issued-to" className="text-right">Destinatário</Label>
              <div className="col-span-3">
                <Select value={bulkVoucherData.issued_to} onValueChange={(value) => handleBulkInputChange('issued_to', value)} disabled={!bulkVoucherData.subscription_id || loadingContacts}>
                  <SelectTrigger><SelectValue placeholder={loadingContacts ? "Carregando contatos..." : "Selecione o destinatário"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum destinatário</SelectItem>
                    {customerContacts.map(contact => (<SelectItem key={contact.id} value={contact.id}>{formatContactName(contact)} {contact.email ? `(${contact.email})` : ''}</SelectItem>))}
                  </SelectContent>
                </Select>
                {bulkVoucherData.subscription_id && customerContacts.length === 0 && !loadingContacts && (<p className="text-xs text-amber-500 mt-1">Nenhum contato encontrado para esta assinatura</p>)}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="bulk-expires" className="text-right">Expiração</Label>
              <div className="col-span-3">
                <Input id="bulk-expires" type="text" value={format(new Date(bulkVoucherData.expires_at), 'dd/MM/yyyy', { locale: ptBR })} readOnly disabled className="bg-gray-50" />
                <p className="text-xs text-gray-500 mt-1">Os vouchers expiram em 365 dias após a criação</p>
              </div>
            </div>
            {isBulkCreating && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${bulkProgress}%` }}></div>
                </div>
                <p className="text-xs text-center mt-2 text-gray-500">Gerando vouchers ({bulkProgress}%)</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsBulkCreateDialogOpen(false)} disabled={isBulkCreating}>Cancelar</Button>
            <Button type="button" onClick={handleBulkCreateVouchers} disabled={isBulkCreating}>{isBulkCreating ? 'Gerando...' : 'Gerar Vouchers'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAssignRecipientDialogOpen} onOpenChange={setIsAssignRecipientDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atribuir Destinatário</DialogTitle>
            <DialogDescription>Selecione o destinatário para o voucher.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="recipient" className="text-right">Destinatário</Label>
              <div className="col-span-3">
                <Select value={selectedRecipient} onValueChange={(value) => setSelectedRecipient(value)}>
                  <SelectTrigger><SelectValue placeholder="Selecione o destinatário" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum destinatário</SelectItem>
                    {customerContacts.map(contact => (<SelectItem key={contact.id} value={contact.id}>{formatContactName(contact)} {contact.email ? `(${contact.email})` : ''}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAssignRecipientDialogOpen(false)} disabled={isAssigningRecipient}>Cancelar</Button>
            <Button type="button" onClick={handleAssignRecipient} disabled={isAssigningRecipient}>
              {isAssigningRecipient ? (<><div className="animate-spin h-4 w-4 mr-2 border-2 border-current border-t-transparent rounded-full"></div>Atribuindo...</>) : 'Atribuir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VoucherCodes; 